// src/components/admin/BookManager.jsx

import React, { useEffect, useState, useCallback, useMemo } from "react";
import BookService from "../../services/dashboard/BookManager";
import AuthorManagerService from "../../services/dashboard/AuthorManager";
import CategoryManagerService from "../../services/dashboard/CategoryManager";
import { useNavigate } from "react-router-dom";
import BackButton from "../common/BackButton";
import { Plus, Pencil, Trash2, Star } from "lucide-react";
import debounce from "lodash.debounce";
import config from "../../config";
import HighlightModal from "./HighlightModal";
import BookHighlightManager from "../../services/dashboard/BookHighlightManager";
import { handleApiResponse } from "../../utils/apiResponseHandler";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { FaFileCsv, FaFileExcel } from "react-icons/fa";

export default function BookManager() {
  const navigate = useNavigate();
  const [books, setBooks] = useState([]);
  const [allAuthors, setAllAuthors] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("ALL");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [booksPage, setBooksPage] = useState({
    content: [],
    totalPages: 0,
    number: 0,
  });
  const [pageSize, setPageSize] = useState(15);
  const PAGE_SIZE = 15;
  const userId = useSelector((state) => state.auth.user?.userId) || null;

  // ─── Sorting State ──────────────────────────────────────────────────────────
  // sortKey: "bookName" | "bookStockStatus" | "bookCopies" | null
  // sortOrder: "asc" | "desc"
  const [sortConfig, setSortConfig] = useState({
    sortKey: null,
    sortOrder: "asc",
  });

  // When a header is clicked, toggle sortKey/sortOrder
  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.sortKey === key && sortConfig.sortOrder === "asc") {
      direction = "desc";
    }
    setSortConfig({ sortKey: key, sortOrder: direction });
  };

  // ─── Fetch Data ──────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchData(page, selectedCategory, pageSize);
  }, [page, selectedCategory, pageSize]);

  const fetchData = async (
    newPage = page,
    category = selectedCategory,
    size = pageSize
  ) => {
    setLoading(true);
    try {
      const [authorRes, categoryRes] = await Promise.all([
        AuthorManagerService.getAllAuthors(),
        CategoryManagerService.getAllCategories(),
      ]);
      const authors = handleApiResponse(authorRes) || [];
      const categories = handleApiResponse(categoryRes) || [];

      setAllAuthors(authors);
      setAllCategories(categories);
      let bookRes;
      if (!category || category === "ALL") {
        bookRes = await BookService.getAllBooks({
          page: newPage,
          size: size,
          userId,
        });
      } else {
        bookRes = await BookService.getBooksByCategory(category);
      }

      const booksData = handleApiResponse(bookRes) || [];
      if (booksData.content) {
        setBooks(booksData.content);
        setTotalPages(booksData.totalPages || 0);
        setPage(booksData.number || 0);
        setBooksPage(booksData);
      } else {
        setBooks(booksData);
        setTotalPages(0);
        setPage(0);
        setBooksPage({ content: booksData, totalPages: 0, number: 0 });
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  // ─── Search (debounced) ──────────────────────────────────────────────────────
  const debouncedSearch = useCallback(
    debounce(async (term) => {
      if (term.trim() === "") {
        fetchData(0);
      } else {
        try {
          const res = await BookService.searchBooks(term);
          const searchResults = handleApiResponse(res) || [];
          setBooks(searchResults);
          setTotalPages(0);
          setPage(0);
        } catch (error) {
          console.error("Search failed:", error);
        }
      }
    }, 300),
    []
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  // ─── Export Helpers ─────────────────────────────────────────────────────────
  const downloadFile = (data, filename, type) => {
    const blob = data instanceof Blob ? data : new Blob([data], { type });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const convertToCSV = (data) => {
    if (!data.length) return "";
    const headers = Object.keys(data[0]);
    const rows = data.map((row) =>
      headers.map((fieldName) => JSON.stringify(row[fieldName] ?? "")).join(",")
    );
    return [headers.join(","), ...rows].join("\n");
  };

  const handleDownloadCSV = async () => {
    try {
      const allBooksData = await BookService.fetchAllBooks();
      if (allBooksData.length > 0) {
        // Transform data to include only essential fields with proper names
        const formattedData = allBooksData.map((book) => ({
          "Book ID": book.bookId,
          "Book Name": book.bookName,
          "Author Name": getAuthorName(book.authorId),
          "Category Name": getCategoryName(book.categoryId),
          "ISBN-10": book.bookIsbn10 || "",
          "ISBN-13": book.bookIsbn13 || "",
          "Published Date": book.publishedDate || "",
          "Pages": book.pages || "",
          "Edition": book.bookEdition || "",
          "Language": book.bookLanguage || "",
          "Status": book.bookStatus || "",
          "Stock Status": book.bookStockStatus || "",
          "Copies Available": book.bookCopies || 0,
          "Average Rating": book.averageRating || 0,
          "Description": book.bookDescription || "",
        }));
        
        const csv = convertToCSV(formattedData);
        downloadFile(csv, "books.csv", "text/csv");
      } else {
        console.warn("No books found to export.");
      }
    } catch (error) {
      console.error("Error downloading CSV:", error);
    }
  };

  const handleDownloadExcel = async () => {
    try {
      const allBooksData = await BookService.fetchAllBooks();
      if (allBooksData.length > 0) {
        // Create a new workbook and worksheet
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Books", {
          views: [{ state: "frozen", xSplit: 0, ySplit: 1 }],
        });

        // Define columns with headers and widths
        worksheet.columns = [
          { header: "Book ID", key: "bookId", width: 10 },
          { header: "Book Name", key: "bookName", width: 40 },
          { header: "Author Name", key: "authorName", width: 25 },
          { header: "Category Name", key: "categoryName", width: 25 },
          { header: "ISBN-10", key: "isbn10", width: 15 },
          { header: "ISBN-13", key: "isbn13", width: 15 },
          { header: "Published Date", key: "publishedDate", width: 15 },
          { header: "Pages", key: "pages", width: 10 },
          { header: "Edition", key: "edition", width: 15 },
          { header: "Language", key: "language", width: 12 },
          { header: "Status", key: "status", width: 12 },
          { header: "Stock Status", key: "stockStatus", width: 15 },
          { header: "Copies Available", key: "copies", width: 15 },
          { header: "Average Rating", key: "rating", width: 15 },
          { header: "Description", key: "description", width: 60 },
        ];

        // Style the header row
        worksheet.getRow(1).font = {
          bold: true,
          color: { argb: "FFFFFFFF" },
          size: 11,
        };
        worksheet.getRow(1).fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF4472C4" },
        };
        worksheet.getRow(1).alignment = {
          vertical: "middle",
          horizontal: "center",
          wrapText: true,
        };
        worksheet.getRow(1).height = 25;

        // Add borders to header
        worksheet.getRow(1).eachCell((cell) => {
          cell.border = {
            top: { style: "thin", color: { argb: "FF000000" } },
            left: { style: "thin", color: { argb: "FF000000" } },
            bottom: { style: "thin", color: { argb: "FF000000" } },
            right: { style: "thin", color: { argb: "FF000000" } },
          };
        });

        // Add data rows
        allBooksData.forEach((book, index) => {
          const row = worksheet.addRow({
            bookId: book.bookId,
            bookName: book.bookName,
            authorName: getAuthorName(book.authorId),
            categoryName: getCategoryName(book.categoryId),
            isbn10: book.bookIsbn10 || "",
            isbn13: book.bookIsbn13 || "",
            publishedDate: book.publishedDate || "",
            pages: book.pages || "",
            edition: book.bookEdition || "",
            language: book.bookLanguage || "",
            status: book.bookStatus || "",
            stockStatus: book.bookStockStatus || "",
            copies: book.bookCopies || 0,
            rating: book.averageRating || 0,
            description: book.bookDescription || "",
          });

          // Apply alternating row colors
          row.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: index % 2 === 0 ? "FFFFFFFF" : "FFF2F2F2" },
          };

          // Apply alignment
          row.alignment = {
            vertical: "top",
            wrapText: true,
          };

          // Center align Book ID
          row.getCell(1).alignment = {
            vertical: "middle",
            horizontal: "center",
          };

          // Add borders to all cells
          row.eachCell((cell) => {
            cell.border = {
              top: { style: "thin", color: { argb: "FFD3D3D3" } },
              left: { style: "thin", color: { argb: "FFD3D3D3" } },
              bottom: { style: "thin", color: { argb: "FFD3D3D3" } },
              right: { style: "thin", color: { argb: "FFD3D3D3" } },
            };
          });
        });

        // Generate Excel file
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        saveAs(blob, "books.xlsx");
      } else {
        console.warn("No books found to export.");
      }
    } catch (error) {
      console.error("Error downloading Excel:", error);
    }
  };

  // ─── Helpers for mapping IDs to names ────────────────────────────────────────
  const getAuthorName = (authorId) => {
    const author = allAuthors.find((a) => a.authorId === authorId);
    return author ? author.authorName : authorId;
  };

  const getCategoryName = (categoryId) => {
    const category = allCategories.find((c) => c.catId === categoryId);
    return category ? category.catName : categoryId;
  };

  // ─── Sorting Logic ───────────────────────────────────────────────────────────
  // Custom order for stock statuses:
  //   index 0 = OUT_OF_STOCK, 1 = LIMITED_STOCK, 2 = IN_STOCK
  const statusOrder = ["OUT_OF_STOCK", "LIMITED_STOCK", "IN_STOCK"];

  const sortedBooks = useMemo(() => {
    if (!sortConfig.sortKey) return books;

    return [...books].sort((a, b) => {
      const { sortKey, sortOrder } = sortConfig;
      let comparison = 0;

      if (sortKey === "bookName") {
        // alphabetical compare
        comparison = a.bookName.localeCompare(b.bookName);
      } else if (sortKey === "bookStockStatus") {
        // use statusOrder index
        const idxA = statusOrder.indexOf(a.bookStockStatus);
        const idxB = statusOrder.indexOf(b.bookStockStatus);
        comparison = idxA - idxB;
      } else if (sortKey === "bookCopies") {
        // numeric compare
        comparison = a.bookCopies - b.bookCopies;
      }

      return sortOrder === "asc" ? comparison : -comparison;
    });
  }, [books, sortConfig]);

  // ─── Highlight Modal (unchanged) ─────────────────────────────────────────────
  const [highlightBook, setHighlightBook] = useState(null);
  const [editingHighlight, setEditingHighlight] = useState(null);
  const openHighlightModal = (book, existingHighlight = null) => {
    setHighlightBook(book);
    setEditingHighlight(existingHighlight);
  };
  const closeHighlightModal = () => {
    setHighlightBook(null);
    setEditingHighlight(null);
  };
  const handleHighlightSave = (data) => {
    const highlightPayload = {
      bookId: highlightBook.bookId,
      book: {
        bookId: highlightBook.bookId,
        title: highlightBook.title,
        description: highlightBook.description,
        price: highlightBook.price,
        authorId: highlightBook.authorId,
        categoryId: highlightBook.categoryId,
        coverImageUrl: highlightBook.coverImageUrl,
      },
      highlightType: data.highlightType,
      startDate: data.startDate,
      endDate: data.endDate,
    };

    if (data.highlightId) {
      BookHighlightManager.updateHighlight(
        data.highlightId,
        highlightPayload
      ).then(() => {
        closeHighlightModal();
        fetchData();
      });
    } else {
      BookHighlightManager.createHighlight(highlightPayload).then(() => {
        closeHighlightModal();
        fetchData();
      });
    }
  };

  // ─── Delete / Edit Handlers ─────────────────────────────────────────────────
  const handleDelete = async (bookId) => {
    if (window.confirm("Are you sure you want to delete this book?")) {
      try {
        const res = await BookService.deleteBook(bookId);
        const success = handleApiResponse(res, "Book deleted successfully!");
        if (success !== null) {
          fetchData(0);
        }
      } catch (error) {
        console.error("Error deleting book:", error);
      }
    }
  };

  const handleEdit = (book) => {
    navigate(`/bookForm/${book.bookId}`, { state: { book } });
  };

  // --- Pagination helpers (add below your state declarations) ---
const handlePageChange = (newPage) => {
  if (newPage < 0 || (totalPages && newPage > totalPages - 1)) return;
  setPage(newPage);
  fetchData(newPage, selectedCategory, pageSize);
  window.scrollTo({ top: 0, behavior: "smooth" });
};

const getVisiblePages = (current, total) => {
  // returns an array like [0,1,2, '...', 10] (0-indexed pages)
  const pages = [];
  if (total <= 7) {
    for (let i = 0; i < total; i++) pages.push(i);
    return pages;
  }
  // always show first two, last two, and window around current
  pages.push(0);
  if (current > 2) pages.push("left-ellipsis");

  const start = Math.max(1, current - 1);
  const end = Math.min(total - 2, current + 1);
  for (let i = start; i <= end; i++) pages.push(i);

  if (current < total - 3) pages.push("right-ellipsis");
  pages.push(total - 1);
  return pages;
};


  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <div className="max-w-[1600px] mx-auto p-6">
        {/* Back Button */}
        <div className="mb-6">
          <BackButton />
        </div>
        
        {/* Header Section */}
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between mb-8">
          {/* Title + Description */}
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-1">
              Books Manager
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Manage and track all books in your inventory
            </p>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Export Dropdown */}
            <div className="relative inline-block">
              <button
                onClick={(e) => {
                  const menu = e.currentTarget.nextElementSibling;
                  menu.classList.toggle('hidden');
                }}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Export
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div className="hidden absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10">
                <button
                  onClick={handleDownloadCSV}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
                >
                  <FaFileCsv className="text-gray-400" />
                  Download CSV
                </button>
                <button
                  onClick={handleDownloadExcel}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
                >
                  <FaFileExcel className="text-gray-400" />
                  Download Excel
                </button>
              </div>
            </div>

            {/* Add Book Button */}
            <button
              onClick={() => navigate("/bookForm")}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Book
            </button>
          </div>
        </div>

        {/* Filter Bar - Enhanced Focus States */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          {/* Search Input */}
          <div className="relative flex-1">
            <svg
              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 1011 18.5a7.5 7.5 0 005.65-2.85z"
              />
            </svg>
            <input
              type="text"
              placeholder="Search books by name, author..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full pl-10 pr-4 py-2.5 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500 transition-all duration-200 shadow-sm hover:border-gray-400 dark:hover:border-gray-600"
            />
          </div>

          {/* Category Filter */}
          <select
            value={selectedCategory}
            onChange={(e) => {
              const selected = e.target.value;
              setSelectedCategory(selected);
              setPage(0);
              fetchData(0, selected);
            }}
            className="px-4 py-2.5 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500 transition-all duration-200 shadow-sm hover:border-gray-400 dark:hover:border-gray-600 cursor-pointer"
          >
            <option value="ALL">All Categories</option>
            {allCategories.map((cat) => (
              <option key={cat.catId} value={cat.catId}>
                {cat.catName}
              </option>
            ))}
          </select>

          {/* Page Size Selector */}
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(0);
              fetchData(0, selectedCategory, Number(e.target.value));
            }}
            className="px-4 py-2.5 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500 transition-all duration-200 shadow-sm hover:border-gray-400 dark:hover:border-gray-600 cursor-pointer"
          >
            {[15, 25, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size} rows
              </option>
            ))}
          </select>
        </div>

        {/* Content Section */}
        {loading ? (
          <p className="text-gray-600 dark:text-gray-400 text-lg animate-pulse">Loading books...</p>
        ) : sortedBooks.length === 0 ? (
          <p className="text-gray-600 dark:text-gray-400 text-lg font-medium">
            No books available.
          </p>
        ) : (
          <>
            {/* Elite Table Container - Enhanced Depth */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="overflow-x-auto p-1">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                {/* Table Header - Light Gray */}
                <thead className="bg-gray-50 dark:bg-gray-900/50">
                  <tr>
                    <th
                      className="px-6 py-4 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider cursor-pointer select-none hover:text-gray-900 dark:hover:text-gray-200 transition-colors"
                      onClick={() => requestSort("bookName")}
                    >
                      <div className="flex items-center gap-2">
                        Book
                        <span className="flex flex-col text-[10px]">
                          <span className={sortConfig.sortKey === "bookName" && sortConfig.sortOrder === "asc" ? "text-gray-900 dark:text-white" : "text-gray-400"}>▲</span>
                          <span className={sortConfig.sortKey === "bookName" && sortConfig.sortOrder === "desc" ? "text-gray-900 dark:text-white" : "text-gray-400"}>▼</span>
                        </span>
                      </div>
                    </th>

                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                      Category
                    </th>

                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                      Language
                    </th>

                    <th
                      className="px-6 py-4 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider cursor-pointer select-none hover:text-gray-900 dark:hover:text-gray-200 transition-colors"
                      onClick={() => requestSort("bookStockStatus")}
                    >
                      <div className="flex items-center gap-2">
                        Status
                        <span className="flex flex-col text-[10px]">
                          <span className={sortConfig.sortKey === "bookStockStatus" && sortConfig.sortOrder === "asc" ? "text-gray-900 dark:text-white" : "text-gray-400"}>▲</span>
                          <span className={sortConfig.sortKey === "bookStockStatus" && sortConfig.sortOrder === "desc" ? "text-gray-900 dark:text-white" : "text-gray-400"}>▼</span>
                        </span>
                      </div>
                    </th>

                    <th
                      className="px-6 py-4 text-right text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider cursor-pointer select-none hover:text-gray-900 dark:hover:text-gray-200 transition-colors"
                      onClick={() => requestSort("bookCopies")}
                    >
                      <div className="flex items-center justify-end gap-2">
                        Copies
                        <span className="flex flex-col text-[10px]">
                          <span className={sortConfig.sortKey === "bookCopies" && sortConfig.sortOrder === "asc" ? "text-gray-900 dark:text-white" : "text-gray-400"}>▲</span>
                          <span className={sortConfig.sortKey === "bookCopies" && sortConfig.sortOrder === "desc" ? "text-gray-900 dark:text-white" : "text-gray-400"}>▼</span>
                        </span>
                      </div>
                    </th>

                    <th className="px-6 py-4 text-center text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>

                {/* Table Body - Elite 2026 Polish */}
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50 bg-white dark:bg-gray-800">
                  {sortedBooks.map((book, idx) => (
                    <tr
                      key={book.bookId}
                      className={`group transition-all duration-200 ease-out hover:bg-gray-50 dark:hover:bg-gray-900/50 cursor-pointer ${
                        idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50/40 dark:bg-gray-900/20'
                      }`}
                    >
                      {/* Book Column - Thumbnail + Title + Author */}
                      <td className="px-6 py-5">
                        <div className="flex items-center gap-4">
                          {/* Thumbnail */}
                          <div className="flex-shrink-0">
                            {book.bookImages?.length > 0 ? (
                              <img
                                src={`${config.url}${book.bookImages[0]}`}
                                alt={book.bookName}
                                className="w-12 h-16 object-cover rounded-md border border-gray-200 dark:border-gray-700 shadow-sm group-hover:shadow-md group-hover:border-gray-300 dark:group-hover:border-gray-600 transition-all duration-200"
                              />
                            ) : (
                              <div className="w-12 h-16 bg-gray-100 dark:bg-gray-700 rounded-md border border-gray-200 dark:border-gray-700 flex items-center justify-center">
                                <span className="text-gray-400 text-xs">No img</span>
                              </div>
                            )}
                          </div>
                          {/* Title + Author */}
                          <div className="flex flex-col min-w-0">
                            <div className="font-semibold text-gray-900 dark:text-white truncate text-[15px] leading-snug">
                              {book.bookName}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400 truncate mt-0.5">
                              {getAuthorName(book.authorId)}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Category - Refined Pill Tag (Softer) */}
                      <td className="px-6 py-5">
                        <span className="inline-flex items-center px-2.5 py-1.5 rounded-md text-xs font-normal bg-blue-50/60 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border border-blue-100 dark:border-blue-800/50 max-w-[150px] truncate">
                          {getCategoryName(book.categoryId)}
                        </span>
                      </td>

                      {/* Language */}
                      <td className="px-6 py-5 text-sm text-gray-600 dark:text-gray-300">
                        {book.bookLanguage}
                      </td>

                      {/* Status - Consistent Badge Sizing */}
                      <td className="px-6 py-5">
                        {book.bookStockStatus === "OUT_OF_STOCK" && (
                          <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800 shadow-sm min-w-[100px] justify-center">
                            Out of Stock
                          </span>
                        )}
                        {book.bookStockStatus === "LIMITED_STOCK" && (
                          <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 border border-yellow-200 dark:border-yellow-800 shadow-sm min-w-[100px] justify-center">
                            Low Stock
                          </span>
                        )}
                        {book.bookStockStatus === "IN_STOCK" && (
                          <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800 shadow-sm min-w-[100px] justify-center">
                            Available
                          </span>
                        )}
                      </td>

                      {/* Copies - Right Aligned */}
                      <td className="px-6 py-5 text-right text-sm font-semibold text-gray-900 dark:text-white">
                        {book.bookCopies}
                      </td>

                      {/* Actions - Improved Icon Visibility (75% opacity default) */}
                      <td className="px-6 py-5">
                        <div className="flex items-center justify-center gap-1.5">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(book);
                            }}
                            className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-all duration-200 hover:scale-105"
                            title="Edit"
                            aria-label="Edit book"
                          >
                            <Pencil size={16} strokeWidth={2} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(book.bookId);
                            }}
                            className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-all duration-200 hover:scale-105"
                            title="Delete"
                            aria-label="Delete book"
                          >
                            <Trash2 size={16} strokeWidth={2} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openHighlightModal(book);
                            }}
                            className="p-2 text-gray-600 hover:text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/30 rounded-lg transition-all duration-200 hover:scale-105"
                            title="Favorite"
                            aria-label="Manage highlights"
                          >
                            <Star size={16} strokeWidth={2} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Elite Pagination - Smooth Transitions */}
          <div className="flex justify-center items-center mt-8">
            <nav className="flex items-center gap-2" aria-label="Pagination">
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 0}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ease-out ${
                  page === 0 
                    ? "bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed" 
                    : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-600 hover:shadow-md hover:-translate-x-0.5"
                }`}
              >
                ← Previous
              </button>

              <div className="flex items-center gap-1.5 px-2">
                {getVisiblePages(page, totalPages).map((p, idx) =>
                  p === "left-ellipsis" || p === "right-ellipsis" ? (
                    <span key={p + idx} className="px-3 py-2 text-sm text-gray-400 dark:text-gray-600">…</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => handlePageChange(p)}
                      className={`min-w-[40px] px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ease-out ${
                        p === page
                          ? "bg-orange-600 text-white shadow-lg hover:bg-orange-700 scale-105 ring-2 ring-orange-500/30"
                          : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-600 hover:shadow-md hover:scale-105"
                      }`}
                      aria-current={p === page ? "page" : undefined}
                    >
                      {p + 1}
                    </button>
                  )
                )}
              </div>

              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={totalPages ? page >= totalPages - 1 : false}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ease-out ${
                  totalPages && page >= totalPages - 1
                    ? "bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed"
                    : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-600 hover:shadow-md hover:translate-x-0.5"
                }`}
              >
                Next →
              </button>
            </nav>
          </div>

        </>
        )}

        {highlightBook && (
        <HighlightModal
          book={highlightBook}
          initialHighlight={editingHighlight}
          isOpen={true}
          onClose={closeHighlightModal}
          onSave={handleHighlightSave}
        />
      )}
      </div>
    </div>
  );
}
